// Firestore Query Optimizations
import { collection, query, where, orderBy, limit, getDocs, onSnapshot, startAfter } from 'firebase/firestore';
import { db } from '@/lib/firebase';

// OPTIMIZATION: Query cache with TTL
class QueryCache {
  constructor(ttl = 5 * 60 * 1000) { // 5 minutes default
    this.cache = new Map();
    this.ttl = ttl;
  }

  getKey(collectionName, constraints) {
    return `${collectionName}_${JSON.stringify(constraints)}`;
  }

  get(collectionName, constraints) {
    const key = this.getKey(collectionName, constraints);
    const cached = this.cache.get(key);
    
    if (cached && Date.now() - cached.timestamp < this.ttl) {
      return cached.data;
    }
    
    if (cached) {
      this.cache.delete(key); // Remove expired entry
    }
    
    return null;
  }

  set(collectionName, constraints, data) {
    const key = this.getKey(collectionName, constraints);
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
    
    // Cleanup old entries (simple LRU)
    if (this.cache.size > 100) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
  }

  clear() {
    this.cache.clear();
  }
}

// Global cache instance
const queryCache = new QueryCache();

// OPTIMIZATION: Optimized query builder
export class OptimizedQuery {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.constraints = [];
    this.useCache = true;
    this.batchSize = 50;
  }

  where(field, operator, value) {
    this.constraints.push({ type: 'where', field, operator, value });
    return this;
  }

  orderBy(field, direction = 'asc') {
    this.constraints.push({ type: 'orderBy', field, direction });
    return this;
  }

  limit(count) {
    this.constraints.push({ type: 'limit', count });
    return this;
  }

  startAfter(doc) {
    this.constraints.push({ type: 'startAfter', doc });
    return this;
  }

  noCache() {
    this.useCache = false;
    return this;
  }

  setBatchSize(size) {
    this.batchSize = size;
    return this;
  }

  // Build the actual Firestore query
  buildQuery() {
    let q = collection(db, this.collectionName);
    
    this.constraints.forEach(constraint => {
      switch (constraint.type) {
        case 'where':
          q = query(q, where(constraint.field, constraint.operator, constraint.value));
          break;
        case 'orderBy':
          q = query(q, orderBy(constraint.field, constraint.direction));
          break;
        case 'limit':
          q = query(q, limit(constraint.count));
          break;
        case 'startAfter':
          q = query(q, startAfter(constraint.doc));
          break;
      }
    });
    
    return q;
  }

  // Execute query with caching
  async execute() {
    const startTime = performance.now();
    
    // Check cache first
    if (this.useCache) {
      const cached = queryCache.get(this.collectionName, this.constraints);
      if (cached) {
        return cached;
      }
    }

    try {
      const q = this.buildQuery();
      const snapshot = await getDocs(q);
      
      const data = {
        docs: snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          _doc: doc // Keep reference for pagination
        })),
        size: snapshot.size,
        empty: snapshot.empty
      };

      // Cache the result
      if (this.useCache) {
        queryCache.set(this.collectionName, this.constraints, data);
      }

      const duration = Math.round(performance.now() - startTime);
      
      return data;
    } catch (error) {
      console.error(`🔧 FIRESTORE ERROR: ${this.collectionName}`, error);
      throw error;
    }
  }

  // Execute with real-time listener (no caching)
  onSnapshot(callback, errorCallback) {
    const q = this.buildQuery();
    return onSnapshot(q, callback, errorCallback);
  }
}

// OPTIMIZATION: Batch queries for related data
export const batchQueries = async (queries) => {
  const startTime = performance.now();
  
  try {
    const results = await Promise.all(
      queries.map(async (queryConfig, index) => {
        try {
          const query = new OptimizedQuery(queryConfig.collection);
          
          // Apply constraints
          if (queryConfig.where) {
            queryConfig.where.forEach(w => query.where(w.field, w.operator, w.value));
          }
          if (queryConfig.orderBy) {
            queryConfig.orderBy.forEach(o => query.orderBy(o.field, o.direction));
          }
          if (queryConfig.limit) {
            query.limit(queryConfig.limit);
          }
          if (queryConfig.noCache) {
            query.noCache();
          }

          const result = await query.execute();
          return { index, success: true, data: result };
        } catch (error) {
          return { index, success: false, error };
        }
      })
    );

    const duration = Math.round(performance.now() - startTime);
    const successCount = results.filter(r => r.success).length;
    
    return results;
  } catch (error) {
    console.error('🔧 FIRESTORE BATCH ERROR:', error);
    throw error;
  }
};

// OPTIMIZATION: Prefetch commonly used data
export const prefetchCommonData = async () => {
  
  const commonQueries = [
    {
      collection: 'categories',
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
      limit: 50
    },
    {
      collection: 'stores',
      where: [{ field: 'active', operator: '==', value: true }],
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
      limit: 20
    },
    {
      collection: 'deliverySettings',
      limit: 10
    }
  ];

  try {
    await batchQueries(commonQueries);
  } catch (error) {
  }
};

// OPTIMIZATION: Clear cache when needed (e.g., after mutations)
export const clearQueryCache = (collectionName = null) => {
  if (collectionName) {
    // Clear specific collection cache
    const keysToDelete = [];
    for (const key of queryCache.cache.keys()) {
      if (key.startsWith(collectionName)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => queryCache.cache.delete(key));
  } else {
    // Clear all cache
    queryCache.clear();
  }
};

// OPTIMIZATION: Helper for common query patterns
export const optimizedQueries = {
  // Get active products with caching
  getActiveProducts: (category = null, limit = 50) => {
    const query = new OptimizedQuery('products')
      .where('active', '==', true)
      .orderBy('createdAt', 'desc')
      .limit(limit);
    
    if (category) {
      query.where('categories', 'array-contains', category);
    }
    
    return query.execute();
  },

  // Get user orders with caching
  getUserOrders: (userId, limit = 20) => {
    return new OptimizedQuery('orders')
      .where('userId', '==', userId)
      .orderBy('createdAt', 'desc')
      .limit(limit)
      .execute();
  },

  // Get store orders with caching
  getStoreOrders: (storeId, status = null, limit = 50) => {
    const query = new OptimizedQuery('orders')
      .where('storeId', '==', storeId)
      .orderBy('createdAt', 'desc')
      .limit(limit);
    
    if (status) {
      query.where('status', '==', status);
    }
    
    return query.execute();
  },

  // Get deliveries for rider with caching
  getRiderDeliveries: (riderId, status = null, limit = 30) => {
    const query = new OptimizedQuery('deliveries')
      .where('riderId', '==', riderId)
      .orderBy('createdAt', 'desc')
      .limit(limit);
    
    if (status) {
      query.where('status', '==', status);
    }
    
    return query.execute();
  }
};

export default OptimizedQuery;
